import { useState, useRef, useEffect, useCallback } from "react";
import {
  Ruler, Minus, MoveVertical, MoveHorizontal, Circle, Square, ArrowUpRight,
  Pencil, MapPin, Triangle, Type, Eraser, Undo2, Redo2, Trash2, Crosshair,
} from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

const TOOLS = [
  ["ruler", Ruler, "Righello / distanza"],
  ["line", Minus, "Linea libera"],
  ["vline", MoveVertical, "Linea verticale"],
  ["hline", MoveHorizontal, "Linea orizzontale"],
  ["ellipse", Circle, "Cerchio / ellisse"],
  ["rect", Square, "Rettangolo"],
  ["arrow", ArrowUpRight, "Freccia"],
  ["pencil", Pencil, "Matita / tratto libero"],
  ["point", MapPin, "Punto di riferimento"],
  ["angle", Triangle, "Angolo"],
  ["text", Type, "Testo / annotazione"],
  ["eraser", Eraser, "Gomma (elimina una)"],
];

const COLORS = ["#34d399", "#fbbf24", "#f87171", "#60a5fa"];

export default function PhotoAnnotator({ value, onChange, testid, readOnly = false }) {
  const wrapRef = useRef(null);
  const [tool, setTool] = useState("line");
  const [color, setColor] = useState(COLORS[0]);
  const [shapes, setShapes] = useState(value || []);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [draft, setDraft] = useState(null);
  const [anglePts, setAnglePts] = useState([]);
  const [size, setSize] = useState({ w: 1, h: 1 });

  useEffect(() => { if (readOnly) setShapes(value || []); }, [readOnly, value]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth || 1, h: el.clientHeight || 1 });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const commit = useCallback((next) => {
    setPast((p) => [...p, shapes]);
    setFuture([]);
    setShapes(next);
    onChange && onChange(next);
  }, [shapes, onChange]);

  const undo = () => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setPast((p) => p.slice(0, -1));
    setFuture((f) => [shapes, ...f]);
    setShapes(prev);
    onChange && onChange(prev);
  };
  const redo = () => {
    if (!future.length) return;
    const nxt = future[0];
    setFuture((f) => f.slice(1));
    setPast((p) => [...p, shapes]);
    setShapes(nxt);
    onChange && onChange(nxt);
  };
  const clearAll = () => { if (shapes.length) commit(shapes.filter((s) => s.type === "calib")); setAnglePts([]); setDraft(null); };

  const rel = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
    };
  };

  const onDown = (e) => {
    if (tool === "eraser") return;
    e.preventDefault();
    const p = rel(e);
    if (tool === "point") { commit([...shapes, { id: uid(), type: "point", x: p.x, y: p.y, color }]); return; }
    if (tool === "text") {
      const t = window.prompt("Testo annotazione:");
      if (t && t.trim()) commit([...shapes, { id: uid(), type: "text", x: p.x, y: p.y, text: t.trim(), color }]);
      return;
    }
    if (tool === "angle") {
      const pts = [...anglePts, p];
      if (pts.length === 3) {
        commit([...shapes, { id: uid(), type: "angle", pts, color }]);
        setAnglePts([]);
      } else setAnglePts(pts);
      return;
    }
    if (tool === "pencil") { setDraft({ id: uid(), type: "pencil", points: [p], color }); return; }
    setDraft({ id: uid(), type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color });
  };

  const onMove = (e) => {
    if (!draft) return;
    const p = rel(e);
    if (draft.type === "pencil") { setDraft({ ...draft, points: [...draft.points, p] }); return; }
    let x2 = p.x, y2 = p.y;
    if (draft.type === "vline") x2 = draft.x1;
    if (draft.type === "hline") y2 = draft.y1;
    setDraft({ ...draft, x2, y2 });
  };

  const onUp = () => {
    if (!draft) return;
    const d = draft;
    setDraft(null);
    if (d.type === "pencil") { if (d.points.length > 1) commit([...shapes, d]); return; }
    if (Math.abs(d.x2 - d.x1) < 0.4 && Math.abs(d.y2 - d.y1) < 0.4) return;
    if (d.type === "ruler" && !calib) {
      const mm = parseFloat(String(window.prompt("Calibrazione: lunghezza reale di questo segmento in millimetri (es. 100)") || "").replace(",", "."));
      if (mm > 0) { commit([...shapes.filter((s) => s.type !== "calib"), { id: uid(), type: "calib", mmPerW: mm / wLen(d) }, d]); return; }
    }
    commit([...shapes, d]);
  };

  const eraseShape = (id) => { if (tool === "eraser") commit(shapes.filter((s) => s.id !== id)); };

  const calib = shapes.find((s) => s.type === "calib");
  const wLen = (s) => Math.hypot((s.x2 - s.x1) / 100, ((s.y2 - s.y1) / 100) * (size.h / size.w)) || 1e-6;
  const mmLabel = (s) => (calib ? `${Math.round(wLen(s) * calib.mmPerW)} mm` : "calibra mm");
  const recalibrate = () => { setTool("ruler"); setAnglePts([]); setDraft(null); if (calib) commit(shapes.filter((s) => s.type !== "calib")); };
  const angleDeg = (pts) => {
    const [a, v, b] = pts;
    const v1 = { x: ((a.x - v.x) / 100) * size.w, y: ((a.y - v.y) / 100) * size.h };
    const v2 = { x: ((b.x - v.x) / 100) * size.w, y: ((b.y - v.y) / 100) * size.h };
    const dot = v1.x * v2.x + v1.y * v2.y;
    const m = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y) || 1;
    return Math.round((Math.acos(Math.max(-1, Math.min(1, dot / m))) * 180) / Math.PI);
  };

  const renderShape = (s, isDraft) => {
    const common = { stroke: s.color, strokeWidth: 2, fill: "none", vectorEffect: "non-scaling-stroke", onPointerDown: () => eraseShape(s.id), style: { cursor: tool === "eraser" ? "pointer" : "crosshair" } };
    if (s.type === "rect") {
      return <rect key={s.id} x={Math.min(s.x1, s.x2)} y={Math.min(s.y1, s.y2)} width={Math.abs(s.x2 - s.x1)} height={Math.abs(s.y2 - s.y1)} {...common} />;
    }
    if (s.type === "ellipse") {
      return <ellipse key={s.id} cx={(s.x1 + s.x2) / 2} cy={(s.y1 + s.y2) / 2} rx={Math.abs(s.x2 - s.x1) / 2} ry={Math.abs(s.y2 - s.y1) / 2} {...common} />;
    }
    if (s.type === "pencil") {
      return <polyline key={s.id} points={s.points.map((p) => `${p.x},${p.y}`).join(" ")} strokeLinejoin="round" strokeLinecap="round" {...common} />;
    }
    if (s.type === "point") {
      return <circle key={s.id} cx={s.x} cy={s.y} r={1.3} fill={s.color} stroke="#000" strokeWidth={0.5} vectorEffect="non-scaling-stroke" onPointerDown={() => eraseShape(s.id)} />;
    }
    if (s.type === "angle") {
      const [a, v, b] = s.pts;
      return <polyline key={s.id} points={`${a.x},${a.y} ${v.x},${v.y} ${b.x},${b.y}`} strokeLinejoin="round" strokeLinecap="round" {...common} />;
    }
    if (s.type === "calib") return null;
    const markerEnd = s.type === "arrow" ? `url(#arrow-${testid})` : undefined;
    return <line key={s.id} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} markerEnd={markerEnd} strokeLinecap="round" {...common} />;
  };

  const labels = shapes.filter((s) => s.type === "text" || s.type === "ruler" || s.type === "angle");

  return (
    <>
      {!readOnly && <div className="absolute inset-x-1 top-1 z-20 flex flex-nowrap items-center gap-1 overflow-x-auto rounded-lg border border-white/10 bg-black/60 p-1 backdrop-blur" data-testid={testid}>
        {TOOLS.map(([id, Icon, title]) => (
          <button key={id} type="button" title={title} onClick={() => { setTool(id); setAnglePts([]); setDraft(null); }}
            data-testid={`${testid}-tool-${id}`}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${tool === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/10 hover:text-foreground"}`}>
            <Icon className="h-3.5 w-3.5" />
          </button>
        ))}
        <button type="button" title={calib ? "Ricalibra righello (mm)" : "Calibra righello (mm)"} onClick={recalibrate} data-testid={`${testid}-calibrate`}
          className={`inline-flex h-7 items-center gap-0.5 rounded-md px-1 text-[10px] font-bold transition-colors ${calib ? "text-primary" : "text-amber-400"} hover:bg-white/10`}>
          <Crosshair className="h-3.5 w-3.5" />mm
        </button>
        <span className="mx-0.5 h-5 w-px bg-white/10" />
        {COLORS.map((c) => (
          <button key={c} type="button" title="Colore" onClick={() => setColor(c)} data-testid={`${testid}-color-${c}`}
            className={`h-5 w-5 rounded-full border-2 transition-transform ${color === c ? "scale-110 border-white" : "border-transparent"}`} style={{ background: c }} />
        ))}
        <span className="mx-0.5 h-5 w-px bg-white/10" />
        <button type="button" title="Annulla" onClick={undo} disabled={!past.length} data-testid={`${testid}-undo`} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-30"><Undo2 className="h-3.5 w-3.5" /></button>
        <button type="button" title="Ripristina" onClick={redo} disabled={!future.length} data-testid={`${testid}-redo`} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-30"><Redo2 className="h-3.5 w-3.5" /></button>
        <button type="button" title="Cancella tutte" onClick={clearAll} disabled={!shapes.some((x) => x.type !== "calib")} data-testid={`${testid}-clear`} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive hover:text-white disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>}

      <div
        ref={wrapRef}
        className={`absolute inset-0 ${readOnly ? "pointer-events-none" : ""}`}
        style={{ touchAction: readOnly ? "auto" : "none", cursor: tool === "eraser" ? "pointer" : "crosshair", zIndex: 5 }}
        onPointerDown={readOnly ? undefined : onDown}
        onPointerMove={readOnly ? undefined : onMove}
        onPointerUp={readOnly ? undefined : onUp}
        onPointerLeave={readOnly ? undefined : onUp}
        data-testid={`${testid}-canvas`}
      >
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <marker id={`arrow-${testid}`} markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L7,3 L0,6 Z" fill={color} />
            </marker>
          </defs>
          {shapes.map((s) => renderShape(s))}
          {draft && renderShape(draft, true)}
          {anglePts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={1} fill="#fff" />)}
        </svg>
        {labels.map((s) => {
          let x, y, txt;
          if (s.type === "text") { x = s.x; y = s.y; txt = s.text; }
          else if (s.type === "ruler") { x = (s.x1 + s.x2) / 2; y = (s.y1 + s.y2) / 2; txt = mmLabel(s); }
          else { x = s.pts[1].x; y = s.pts[1].y; txt = `${angleDeg(s.pts)}°`; }
          return (
            <span key={`l-${s.id}`} className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-black/70 px-1 py-0.5 text-[10px] font-bold" style={{ left: `${x}%`, top: `${y}%`, color: s.color }}>{txt}</span>
          );
        })}
      </div>
    </>
  );
}
